import { GROUND } from "./rig";
import { add, f2, lerpP, limb, path, BOOT, BOOT_COLLAR, BOOT_SOLE, BOOT_TOE, FOOT, SANDAL } from "./geom";

const poly = (pts) => `M${pts.map(f2).join(" L")}Z`;
const HemTrim = ({ A, H, half, color }) => {
  const L = Math.hypot(H[0] - A[0], H[1] - A[1]) || 1, n = [-(H[1] - A[1]) / L, (H[0] - A[0]) / L], u = [(H[0] - A[0]) / L, (H[1] - A[1]) / L], h = add(H, u, -3);
  return <path d={`M${f2(add(h, n, -half))} L${f2(add(h, n, half))}`} stroke={color} strokeWidth="3.2" strokeLinecap="round" strokeDasharray="5 3" />;
};
const fillOf = (ch, pid, far) => (ch.bottomPattern ? `url(#wax-${pid})` : far ? ch.pal.bottomB : ch.pal.bottom);

const BootProfile = ({ g, P, far }) => (
  <>
    <path d={path(BOOT_SOLE, g.T)} fill={P.shoeB} />
    <path d={path(BOOT, g.T)} fill={P.shoe} />
    <path d={path(BOOT_TOE, g.T)} fill={P.shoeB} opacity=".85" />
    <path d={path(BOOT_COLLAR, g.T)} fill={P.gear} />
    <path d={path([["M", [-12, 6]], ["C", [4, 3], [20, 7], [34, 12]]], g.T)} stroke={P.accent} strokeWidth="3" fill="none" strokeLinecap="round" />
    {[[10, -16], [13, -9], [18, -4], [23, -1]].map(([u, v]) => <path key={u} d={path([["M", [u - 3, v - 2]], ["L", [u + 3, v + 2]]], g.T)} stroke={P.accent} strokeWidth="2" strokeLinecap="round" />)}
    <path d={path([["M", [-17, 19]], ["L", [53, 19]]], g.T)} stroke={P.accent} strokeWidth="1.2" strokeDasharray="3 4" opacity=".7" />
    {far && <path d={path(BOOT, g.T)} fill="#000" opacity=".16" />}
  </>
);

const SandalProfile = ({ g, P, far, high }) => (
  <>
    <path d={path(SANDAL, g.T)} fill={P.shoeB} />
    <path d={path(FOOT, g.T)} fill={far ? P.skinB : P.skin} />
    <path d={path([["M", [-2, 15.5]], ["C", [8, 12.5], [20, 12.5], [28, 15.5]]], g.T)} stroke={P.hi} strokeWidth="1.5" fill="none" opacity=".7" />
    <path d={path([["M", [40, 11]], ["C", [41, 13], [41, 15], [40, 16.5]]], g.T)} stroke={P.skinB} strokeWidth="1.4" fill="none" />
    <path d={path([["M", [4, -3]], ["C", [11, 3], [14, 9], [13, 15]]], g.T)} stroke={P.accent} strokeWidth="4" fill="none" strokeLinecap="round" />
    <path d={path([["M", [30, 9]], ["C", [33, 12], [34, 15], [34, 17]]], g.T)} stroke={P.trim} strokeWidth="3" fill="none" strokeLinecap="round" />
    {high && [-12, -4].map((v) => <path key={v} d={path([["M", [-13, v]], ["L", [8, v - 1]]], g.T)} stroke={P.accent} strokeWidth="3.5" strokeLinecap="round" />)}
    <circle cx={g.T([2, -2])[0]} cy={g.T([2, -2])[1]} r="3.6" fill={P.hi} opacity=".7" />
  </>
);

export const LegProfile = ({ g, far, ch, pid }) => {
  const P = ch.pal, w = ch.build, skin = far ? P.skinB : P.skin, hem = lerpP(g.hip, g.knee, ch.bottomLen || 0);
  return (
    <g>
      <path d={limb(g.knee, g.ankle, 24 * w, 13 * w, { at: 0.32, front: 1, back: 6 })} fill={skin} />
      <path d={limb(g.hip, g.knee, 32 * w, 23 * w, { at: 0.45, front: 4, back: 2 })} fill={skin} />
      <ellipse cx={g.knee[0] + 5} cy={g.knee[1]} rx="8" ry="9" fill={far ? P.skinB : P.hi} opacity=".55" />
      {!ch.skirt && <>
        <path d={limb(g.hip, hem, 40 * w, 33 * w, { front: 3, back: 3 })} fill={fillOf(ch, pid, far)} />
        {far && ch.bottomPattern && <path d={limb(g.hip, hem, 40 * w, 33 * w, { front: 3, back: 3 })} fill="#000" opacity=".18" />}
        <HemTrim A={g.hip} H={hem} half={17 * w} color={P.accent} />
      </>}
      {ch.footwear === "boots" && P.sock && <path d={limb(lerpP(g.knee, g.ankle, 0.62), g.ankle, 17 * w, 15 * w)} fill={P.sock} />}
      {ch.footwear === "boots" ? <BootProfile g={g} P={P} far={far} /> : <SandalProfile g={g} P={P} far={far} high={ch.footwear === "sandalboots"} />}
    </g>
  );
};

const FootFront = ({ A, s, P, kind }) => {
  const X = (x) => A[0] + x * s, y = (v) => A[1] + v;
  if (kind === "boots") return (
    <>
      {P.sock && <rect x={A[0] - 9} y={y(-36)} width="18" height="14" rx="5" fill={P.sock} />}
      <path d={`M${X(-11)} ${y(-26)} L${X(11)} ${y(-26)} L${X(12)} ${y(6)} C${X(17)} ${y(10)} ${X(18)} ${y(16)} ${X(17)} ${y(20)} L${X(-17)} ${y(20)} C${X(-18)} ${y(14)} ${X(-16)} ${y(8)} ${X(-12)} ${y(4)}Z`} fill={P.shoe} />
      <ellipse cx={A[0]} cy={y(15)} rx="15" ry="6" fill={P.shoeB} opacity=".85" />
      <rect x={A[0] - 18} y={y(18)} width="36" height="5" rx="2.5" fill={P.shoeB} />
      <rect x={A[0] - 12} y={y(-27)} width="24" height="6" rx="3" fill={P.gear} />
      {[-16, -10, -4].map((v) => <path key={v} d={`M${A[0] - 5} ${y(v)} L${A[0] + 5} ${y(v)}`} stroke={P.accent} strokeWidth="2" strokeLinecap="round" />)}
    </>
  );
  return (
    <>
      <rect x={A[0] - 17} y={y(19)} width="34" height="5" rx="2.5" fill={P.shoeB} />
      <path d={`M${X(-9)} ${y(-6)} L${X(9)} ${y(-6)} C${X(12)} ${y(4)} ${X(15)} ${y(12)} ${X(15)} ${y(17)} C${X(15)} ${y(21)} ${X(-15)} ${y(21)} ${X(-15)} ${y(17)} C${X(-15)} ${y(12)} ${X(-12)} ${y(4)} ${X(-9)} ${y(-6)}Z`} fill={P.skin} />
      {[[-11, 4.2], [-5, 3.4], [1, 3.1], [6.5, 2.8], [11.5, 2.5]].map(([x, rr]) => <circle key={x} cx={X(x)} cy={y(18)} r={rr} fill={P.hi} opacity=".65" />)}
      <path d={`M${X(-13)} ${y(7)} L${X(13)} ${y(7)}`} stroke={P.accent} strokeWidth="4" strokeLinecap="round" />
      {kind === "sandalboots" && [-10, -3].map((v) => <path key={v} d={`M${A[0] - 10} ${y(v)} L${A[0] + 10} ${y(v)}`} stroke={P.accent} strokeWidth="3.5" strokeLinecap="round" />)}
    </>
  );
};

export const LegFront = ({ g, ch, pid, back }) => {
  const P = ch.pal, w = ch.build, hem = lerpP(g.hip, g.knee, ch.bottomLen || 0);
  return (
    <g>
      <path d={limb(g.knee, g.ankle, 24 * w, 14 * w, { at: 0.3, front: 4, back: 2 })} fill={P.skin} />
      <path d={limb(g.hip, g.knee, 33 * w, 24 * w, { at: 0.45, front: 2, back: 3 })} fill={P.skin} />
      {!back && <ellipse cx={g.knee[0]} cy={g.knee[1] + 2} rx="9" ry="8" fill={P.hi} opacity=".5" />}
      {!ch.skirt && <path d={limb(g.hip, hem, 42 * w, 35 * w)} fill={fillOf(ch, pid)} />}
      <FootFront A={g.ankle} s={g.sgn} P={P} kind={ch.footwear} />
    </g>
  );
};

export const Arm = ({ a, far, ch }) => {
  const P = ch.pal, w = ch.build, skin = far ? P.skinB : P.skin, sleeve = ch.top === "wrap" ? P.top : far ? P.topB : P.top, cuff = lerpP(a.el, a.wr, 0.72);
  return (
    <g>
      <path d={limb(a.el, a.wr, 17 * w, 12 * w, { front: 1, back: 2 })} fill={skin} />
      <path d={limb(a.sh, a.el, 21 * w, 17 * w, { front: 2, back: 2 })} fill={skin} />
      <path d={limb(a.sh, lerpP(a.sh, a.el, ch.top === "apron" ? 0.62 : 0.45), 28 * w, 24 * w)} fill={sleeve} />
      <circle cx={cuff[0]} cy={cuff[1]} r={8 * w} fill="none" stroke={ch.extras.includes("cuffs") ? P.trim : P.gear} strokeWidth="3.2" />
      <circle cx={a.wr[0]} cy={a.wr[1]} r={9.5 * w} fill={skin} />
    </g>
  );
};

export const Torso = ({ g, ch, view, pid }) => {
  const P = ch.pal, { pelvis, neck, up } = g, n = [-up[1], up[0]], hem = add(pelvis, up, -16), s = ch.build, front = view !== "profile";
  const pts = [add(hem, n, -30 * s), add(hem, n, 30 * s), add(neck, n, 24 * s), add(neck, n, -24 * s)];
  const mid = lerpP(hem, neck, 0.5), chest = lerpP(pelvis, neck, 0.72), apronLow = add(pelvis, up, -46);
  const body = `M${f2(pts[0])} Q${f2(add(mid, n, -34 * s))} ${f2(pts[3])} L${f2(pts[2])} Q${f2(add(mid, n, 34 * s))} ${f2(pts[1])} Z`;
  return (
    <g>
      <path d={body} fill={ch.top === "wrap" ? `url(#wax-${pid})` : P.top} />
      {ch.top === "shirt" && view === "front" && <path d={`M${f2(add(neck, n, -13))} L${f2(lerpP(neck, pelvis, 0.2))} L${f2(add(neck, n, 13))}`} stroke={P.accent} strokeWidth="3" fill="none" strokeLinejoin="round" />}
      {ch.top === "vest" && (front
        ? [-1, 1].map((k) => <g key={k}><path d={poly([add(neck, n, k * 24), add(neck, n, k * 7), add(hem, n, k * 7), add(hem, n, k * 30)])} fill={P.vest} /><rect x={add(lerpP(hem, neck, 0.3), n, k * 18)[0] - 6} y={lerpP(hem, neck, 0.3)[1] - 5} width="12" height="10" rx="2" fill="none" stroke={P.trim} strokeWidth="1.6" /></g>)
        : <path d={poly([add(neck, n, -24), add(neck, n, 8), add(hem, n, 10), add(hem, n, -30)])} fill={P.vest} />)}
      {ch.top === "apron" && view !== "back" && (front
        ? <><path d={poly([add(chest, n, -20), add(chest, n, 20), add(apronLow, n, 27), add(apronLow, n, -27)])} fill={P.apron} /><path d={`M${f2(add(chest, n, -18))} L${f2(add(neck, n, -6))} M${f2(add(chest, n, 18))} L${f2(add(neck, n, 6))}`} stroke={P.apron} strokeWidth="4" />{[[-8, 0.3], [10, 0.55]].map(([x, t]) => <circle key={x} cx={add(lerpP(apronLow, chest, t), n, x)[0]} cy={lerpP(apronLow, chest, t)[1]} r="4" fill={P.topB} opacity=".45" />)}</>
        : <path d={poly([add(chest, n, 16), add(chest, n, 27), add(apronLow, n, 34), add(apronLow, n, 14)])} fill={P.apron} />)}
      {ch.extras.includes("sash") && <>
        <path d={`M${f2(add(add(pelvis, up, 4), n, -29))} L${f2(add(add(pelvis, up, 4), n, 29))}`} stroke={P.accent} strokeWidth="10" />
        <path d={`M${f2(add(add(pelvis, up, 2), n, front ? 18 : -22))} l${front ? 4 : -6} 34`} stroke={P.accent} strokeWidth="7" strokeLinecap="round" />
      </>}
      {!ch.extras.includes("sash") && ch.top !== "apron" && <path d={`M${f2(add(hem, up, 6))} m-26 0 l6 -6 l6 6 l6 -6 l6 6 l6 -6 l6 6 l6 -6 l6 6`} stroke={P.trim} strokeWidth="2.4" fill="none" />}
      {ch.extras.includes("backpack") && view !== "back" && (front
        ? [-1, 1].map((k) => <path key={k} d={`M${f2(add(neck, n, k * 17))} L${f2(add(lerpP(neck, pelvis, 0.55), n, k * 21))}`} stroke={P.gear} strokeWidth="6" strokeLinecap="round" />)
        : <path d={`M${f2(add(neck, n, 4))} L${f2(add(lerpP(neck, pelvis, 0.5), n, 9))}`} stroke={P.gear} strokeWidth="5" strokeLinecap="round" />)}
      {ch.extras.includes("satchel") && view !== "back" && <path d={`M${f2(add(neck, n, front ? -18 : -6))} L${f2(add(pelvis, n, front ? 24 : 4))}`} stroke={P.gear} strokeWidth="4" strokeLinecap="round" />}
      {view === "front" && <Jewel g={g} ch={ch} n={n} />}
    </g>
  );
};

const Jewel = ({ g, ch, n }) => {
  const P = ch.pal, at = lerpP(g.neck, g.pelvis, 0.24);
  if (ch.extras.includes("beads")) return <g>{Array.from({ length: 9 }, (_, i) => { const t = (i - 4) / 4, p = add(add(g.neck, n, t * 16), [0, 6 + (1 - t * t) * 12]); return <circle key={i} cx={p[0]} cy={p[1]} r="3.2" fill={i % 2 ? P.accent : P.trim} />; })}</g>;
  if (!ch.extras.some((e) => e === "pendant" || e === "compass")) return null;
  return <g><path d={`M${f2(add(g.neck, n, -9))} L${f2(at)} L${f2(add(g.neck, n, 9))}`} stroke={P.gear} strokeWidth="1.6" fill="none" /><circle cx={at[0]} cy={at[1]} r="5.5" fill={ch.extras.includes("compass") ? P.trim : P.accent} stroke={P.trim} strokeWidth="1.6" /></g>;
};

export const Skirt = ({ g, ch, view, pid }) => {
  if (!ch.skirt) return null;
  const { pelvis, up, legs } = g, n = [-up[1], up[0]], waist = add(pelvis, up, 8), hemY = pelvis[1] + ch.skirt, ks = [legs.L.knee, legs.R.knee];
  const fr = Math.max(pelvis[0] + 32, ...ks.map((k) => k[0] + (view === "profile" ? 18 : 24))), bk = Math.min(pelvis[0] - 32, ...ks.map((k) => k[0] - (view === "profile" ? 16 : 24)));
  const a = add(waist, n, -24 * ch.build), b = add(waist, n, 24 * ch.build);
  const d = `M${f2(a)} Q${f2([bk - 2, (waist[1] + hemY) / 2])} ${bk} ${hemY} L${fr} ${hemY} Q${f2([fr + 2, (waist[1] + hemY) / 2])} ${f2(b)} Z`;
  return (
    <g>
      <path d={d} fill={ch.bottomPattern ? `url(#wax-${pid})` : ch.pal.bottom} />
      <path d={`M${bk + 2} ${hemY - 5} L${fr - 2} ${hemY - 5}`} stroke={ch.pal.trim} strokeWidth="3" />
      <path d={`M${f2(a)} L${f2(b)}`} stroke={ch.pal.accent} strokeWidth="6" />
    </g>
  );
};

export const CapeBack = ({ g, ch, view }) => {
  if (ch.top !== "cape") return null;
  const P = ch.pal, { pelvis, neck, up } = g, n = [-up[1], up[0]], low = add(pelvis, up, -72);
  const d = view === "profile"
    ? poly([add(neck, n, -4), add(neck, n, -22), add(low, n, -52), add(low, n, -8)])
    : poly([add(neck, n, -30), add(neck, n, 30), add(low, n, 56), add(low, n, -56)]);
  return <g><path d={d} fill={view === "back" ? P.cape : P.capeB} strokeLinejoin="round" stroke={view === "back" ? P.cape : P.capeB} strokeWidth="8" />
    <path d={view === "profile" ? `M${f2(add(low, n, -50))} L${f2(add(low, n, -10))}` : `M${f2(add(add(low, up, 8), n, -52))} L${f2(add(add(low, up, 8), n, 52))}`} stroke={P.trim} strokeWidth="3" strokeDasharray="6 4" />
    {view === "back" && <path d={`M${f2(lerpP(low, neck, 0.2))} L${f2(lerpP(low, neck, 0.75))} M${f2(add(lerpP(low, neck, 0.5), n, -14))} L${f2(lerpP(low, neck, 0.62))} L${f2(add(lerpP(low, neck, 0.5), n, 14))}`} stroke={P.trim} strokeWidth="3" fill="none" strokeLinecap="round" />}
  </g>;
};

export const Backpack = ({ g, ch, view }) => {
  if (!ch.extras.includes("backpack") || view === "front") return null;
  const P = ch.pal, { pelvis, neck, up } = g, n = [-up[1], up[0]], c = view === "profile" ? add(lerpP(pelvis, neck, 0.6), n, -32) : lerpP(pelvis, neck, 0.55);
  const hw = view === "profile" ? 15 : 30, hh = 36;
  const box = poly([add(add(c, up, hh), n, -hw), add(add(c, up, hh), n, hw), add(add(c, up, -hh), n, hw), add(add(c, up, -hh), n, -hw)]);
  return (
    <g>
      <path d={box} fill={P.gear} stroke={P.gear} strokeWidth="10" strokeLinejoin="round" />
      <path d={poly([add(add(c, up, hh), n, -hw + 2), add(add(c, up, hh), n, hw - 2), add(add(c, up, 8), n, hw - 2), add(add(c, up, 8), n, -hw + 2)])} fill="#000" opacity=".14" />
      {view === "back" && <><circle cx={c[0]} cy={c[1] - 6} r="11" fill="none" stroke={P.accent} strokeWidth="2.4" /><path d={`M${c[0]} ${c[1] + 5} L${c[0]} ${c[1] - 15} M${c[0] - 6} ${c[1] - 9} L${c[0]} ${c[1] - 3} L${c[0] + 6} ${c[1] - 9}`} stroke={P.accent} strokeWidth="2.2" fill="none" /></>}
    </g>
  );
};

export const HandProps = ({ g, ch, view, pid, layer }) => {
  const P = ch.pal, wr = g.arms.R.wr, e = ch.extras;
  if (layer === "under") return (
    <g>
      {e.includes("cane") && <><path d={`M${wr[0] + 2} ${wr[1] - 10} L${wr[0] + (view === "profile" ? 14 : 4)} ${GROUND}`} stroke={P.gear} strokeWidth="5.5" strokeLinecap="round" /><circle cx={wr[0] + 2} cy={wr[1] - 12} r="5" fill={P.trim} /></>}
      {e.includes("pot") && <g><ellipse cx={wr[0] + 8} cy={wr[1] + 10} rx="17" ry="15" fill={P.apron} /><rect x={wr[0] - 1} y={wr[1] - 8} width="18" height="7" rx="3" fill={P.apron} /><path d={`M${wr[0] - 6} ${wr[1] + 10} Q${wr[0] + 8} ${wr[1] + 16} ${wr[0] + 22} ${wr[1] + 10}`} stroke={P.trim} strokeWidth="2" fill="none" /></g>}
      {e.includes("satchel") && view !== "back" && <rect x={(view === "profile" ? g.pelvis[0] - 6 : add(g.pelvis, [-g.up[1], g.up[0]], 24)[0] - 10)} y={g.pelvis[1] - 4} width="20" height="17" rx="4" fill={P.gear} stroke={P.trim} strokeWidth="1.4" />}
    </g>
  );
  if (!e.includes("seed")) return null;
  const s = [wr[0] + 6, wr[1] - 18];
  return <g className="seed-glow"><circle cx={s[0]} cy={s[1]} r="22" fill={`url(#glow-${pid})`} /><ellipse cx={s[0]} cy={s[1]} rx="5.5" ry="7.5" fill={P.trim} /><path d={`M${s[0]} ${s[1] - 7} q4 -6 9 -5`} stroke={P.leaf} strokeWidth="2.5" fill="none" strokeLinecap="round" /></g>;
};

export const Defs = ({ ch, pid }) => (
  <defs>
    <pattern id={`wax-${pid}`} width="16" height="16" patternUnits="userSpaceOnUse">
      <rect width="16" height="16" fill={ch.pal.bottom} />
      <circle cx="8" cy="8" r="5" fill="none" stroke={ch.pal.waxRing || ch.pal.trim} strokeWidth="2" />
      <circle cx="8" cy="8" r="1.6" fill={ch.pal.trim} /><circle cx="0" cy="0" r="2" fill={ch.pal.accent} /><circle cx="16" cy="16" r="2" fill={ch.pal.accent} />
    </pattern>
    <radialGradient id={`glow-${pid}`}><stop offset="0" stopColor="#FFF4C2" stopOpacity=".95" /><stop offset=".45" stopColor="#4FD1C5" stopOpacity=".55" /><stop offset="1" stopColor="#17A2A2" stopOpacity="0" /></radialGradient>
  </defs>
);
